import { Fragment, useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Check, ChevronDown, ChevronRight, ChevronUp, Download, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import OrderJourneyTrail from './OrderJourneyTrail';
import type { CollectionCondition, CollectionStage, OrdersDailyRow, OrdersDailyStatusBucket } from '../../types/ordersDailyTruth';
import {
  formatLifts,
  formatOrdersDue,
  formatOrdersEtaOnTime,
  describeGpsCheck,
  describeTapIssues,
  formatOrdersLocalWallInstant,
  formatOrdersImpact,
  formatOrdersPod,
  formatOrdersService,
  matchesOrdersDailyKpi,
  ordersDailyRowSearchText,
  COLLECTION_CONDITION_HINTS,
  COLLECTION_CONDITION_LABELS,
  collectionProvenanceNote,
  ordersDailyStatusLabel,
  partialDeliveryDetail,
  partialDeliveryFraction,
  sortOrdersDailyRows,
  sourceStatusContradiction,
  type OrdersDailyKpiFilter,
  type OrdersDailySortDirection,
  type OrdersDailySortField,
  bookingCustomerText,
} from '../../utils/ordersDailyPresentation';
import { UNKNOWN_CELL } from '../../utils/uiText';

interface DepotIndex {
  owned: Set<string>;
  names: Map<string, string>;
}

const TABLE_COLUMNS: Array<{
  field: OrdersDailySortField;
  label: string;
  align: 'left' | 'right';
}> = [
  { field: 'tracking_id', label: 'Tracking ID', align: 'left' },
  { field: 'consignment_no', label: 'Consignment', align: 'left' },
  { field: 'booking_customer', label: 'Customer', align: 'left' },
  { field: 'customer', label: 'Collected from', align: 'left' },
  { field: 'cust_ref', label: 'Customer ref', align: 'left' },
  { field: 'destination_postal_code', label: 'Delivery Postcode', align: 'left' },
  { field: 'direction', label: 'Dir', align: 'left' },
  { field: 'type', label: 'Type', align: 'left' },
  { field: 'col_depot', label: 'Collection depot', align: 'left' },
  { field: 'del_depot', label: 'Delivery depot', align: 'left' },
  { field: 'service', label: 'Service', align: 'left' },
  { field: 'due', label: 'Due', align: 'left' },
  { field: 'lifts', label: 'Lifts', align: 'right' },
  { field: 'status', label: 'Status', align: 'left' },
  { field: 'pod', label: 'POD', align: 'left' },
  { field: 'eta_on_time', label: 'On-time', align: 'left' },
  { field: 'eta_at', label: 'ETA', align: 'left' },
  { field: 'gps', label: 'GPS', align: 'left' },
  { field: 'impact', label: '£ impact', align: 'right' },
];

/** Sticky column widths — keep th/td left offsets in sync (literal classes for Tailwind). */
const STICKY_HEADER_BG = 'bg-background';
const STICKY_EDGE_SHADOW = 'shadow-panel';

/** Opaque only — translucent sticky cells let scrolled columns bleed through. */
function stickyBodyBg(expanded: boolean): string {
  return expanded
    ? 'bg-popover group-hover:bg-popover'
    : 'bg-background group-hover:bg-secondary';
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Click copies the ID; does not expand/collapse the row. */
function CopyableOrderId({
  value,
  className,
}: {
  value: string;
  className: string;
}) {
  const [copied, setCopied] = useState(false);

  const onClick = useCallback(async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!value || value === '—') return;
    const ok = await copyText(value);
    if (!ok) return;
    setCopied(true);
  }, [value]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!value || value === '—') {
    return <span className={className}>—</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? 'Copied' : `Click to copy ${value}`}
      className={`inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded ${className}`}
    >
      <span>{value}</span>
      {copied && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
    </button>
  );
}

const STATUS_BUCKET_TAG_CLASS: Record<OrdersDailyStatusBucket, string> = {
  delivered: 'bg-success/15 text-success border-success/30',
  partial: 'bg-chart-9/15 text-chart-9 border-chart-9/30',
  out_for_delivery: 'bg-chart-10/15 text-chart-10 border-chart-10/30',
  at_depot: 'bg-warning/15 text-warning border-warning/30',
  in_transit: 'bg-chart-5/15 text-chart-5 border-chart-5/30',
  removed_from_plan: 'bg-chart-2/15 text-chart-2 border-chart-2/30',
  exception: 'bg-destructive/15 text-destructive border-destructive/30',
  unmapped: 'bg-secondary/15 text-foreground border-border/30',
};

const COLLECTION_CONDITION_TEXT: Record<CollectionCondition, string> = {
  on_track: 'text-muted-foreground',
  at_risk: 'text-warning',
  overdue: 'text-warning',
  needs_investigation: 'text-destructive',
  expected_exception: 'text-muted-foreground',
};

const COLLECTION_STAGE_TAG_CLASS: Record<CollectionStage, string> = {
  awaiting_collection: 'bg-secondary/15 text-foreground border-border/30',
  collected: 'bg-chart-1/15 text-chart-1 border-chart-1/30',
  at_collection_depot: 'bg-warning/15 text-warning border-warning/30',
  dispatched_onward: 'bg-success/15 text-success border-success/30',
};

/** Collections are coloured by their own stage, deliveries by status bucket. */
function statusTagClass(row: OrdersDailyRow): string {
  if (row.direction === 'collection') {
    return row.collection_stage
      ? COLLECTION_STAGE_TAG_CLASS[row.collection_stage]
      : STATUS_BUCKET_TAG_CLASS.unmapped;
  }
  return STATUS_BUCKET_TAG_CLASS[row.status_bucket];
}

function DepotEnd({ number, index }: { number?: string; index: DepotIndex }) {
  if (!number) return <span>—</span>;
  const owned = index.owned.has(number);
  const name = index.names.get(number);
  return (
    <span className={owned ? 'text-success font-semibold' : 'text-muted-foreground'}>
      {number}{name ? ` ${name}` : ''}
    </span>
  );
}

const GPS_TONE_CLASS = {
  agrees: 'text-success',
  disagrees: 'text-warning font-semibold',
  reading: 'text-foreground',
  none: 'text-muted-foreground font-normal',
} as const;

/** The van's GPS beside the Palletways ETA verdict: confirmed, disagreeing, or why there is no reading. */
function GpsCheckCell({ row }: { row: OrdersDailyRow }) {
  const check = describeGpsCheck(row);
  if (!check) return <td className="py-2.5 px-2.5 text-xs text-muted-foreground/70">{UNKNOWN_CELL}</td>;
  return (
    <td className={`py-2.5 px-2.5 whitespace-nowrap text-xs ${GPS_TONE_CLASS[check.tone]}`} title={check.tooltip}>
      {check.tone === 'agrees' ? '✓ ' : ''}{check.headline}
      {check.detail ? (
        <span className="block text-xs font-normal text-muted-foreground tabular-nums">{check.detail}</span>
      ) : null}
      {check.issues.map((issue) => (
        <span key={issue} className="block text-xs font-normal text-warning/90">⚠ {issue}</span>
      ))}
    </td>
  );
}

function downloadCsv(rows: OrdersDailyRow[], filename: string) {
  const headers = [
    'Tracking ID', 'Consignment', 'Customer', 'Collected from', 'Customer ref', 'Delivery Postcode', 'Dir', 'Type', 'Collect depot', 'Deliver depot',
    'Service', 'Due', 'Lifts', 'Status', 'POD', 'On-time', 'ETA', 'ETA window end', 'ETA basis', 'ETA judged on', 'ETA judged at', 'GPS on-time', 'GPS arrived', 'GPS left', 'GPS match', 'GPS found at', 'Tap issues', '£ impact',
    'Lifts delivered', 'Lifts outstanding', 'Source status', 'Collection condition', 'Booked by depot',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((row) => {
      const cells = [
        row.tracking_id,
        row.consignment_no || '',
        bookingCustomerText(row) || '',
        row.customer || '',
        row.cust_ref || '',
        row.destination_postal_code || '',
        row.direction === 'collection' ? 'COL' : 'DEL',
        row.type || '',
        row.col_depot || '',
        row.del_depot || '',
        formatOrdersService(row),
        formatOrdersDue(row.due_date, row.due_time),
        row.lifts ?? '',
        ordersDailyStatusLabel(row),
        row.missed_am ? 'Missed' : formatOrdersPod(row.pod_date, row.pod_time),
        formatOrdersEtaOnTime(row.eta_on_time),
        row.eta_at || '',
        row.eta_window_end || '',
        row.eta_basis || '',
        row.eta_actual_basis || '',
        row.eta_actual_at || '',
        formatOrdersEtaOnTime(row.eta_gps_on_time),
        row.gps_arrived_at || '',
        row.gps_departed_at || '',
        row.gps_match_status || '',
        row.gps_site_source || '',
        describeTapIssues(row).join('; '),
        formatOrdersImpact(row),
        row.partial_delivery ? (row.lifts_delivered ?? '') : '',
        row.partial_delivery ? (row.lifts_outstanding ?? '') : '',
        row.source_status || '',
        row.collection_condition ? COLLECTION_CONDITION_LABELS[row.collection_condition] : '',
        row.booking_depot || '',
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    }),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface OrdersDailyDrilldownTableProps {
  rows: OrdersDailyRow[];
  loading: boolean;
  emptyMessage: string;
  filter: OrdersDailyKpiFilter;
  exportName: string;
  /** When set, seeds the search box — used to jump to one consignment. */
  searchTerm?: string;
}

export default function OrdersDailyDrilldownTable({
  rows,
  loading,
  emptyMessage,
  filter,
  exportName,
  searchTerm,
}: OrdersDailyDrilldownTableProps) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (searchTerm != null) setQuery(searchTerm);
  }, [searchTerm]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sortField, setSortField] = useState<OrdersDailySortField>('tracking_id');
  const [sortDirection, setSortDirection] = useState<OrdersDailySortDirection>('asc');
  const [depots, setDepots] = useState<DepotIndex>({ owned: new Set(), names: new Map() });

  useEffect(() => {
    let cancelled = false;
    void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.depots())
      .then(async (res) => {
        if (!res.ok) return;
        const json = await res.json() as { depots?: Array<{ name?: string; depotNumber?: string; owned?: boolean }> };
        const owned = new Set<string>();
        const names = new Map<string, string>();
        for (const depot of json.depots ?? []) {
          const number = String(depot.depotNumber ?? '').trim();
          if (!number) continue;
          if (depot.owned === true) owned.add(number);
          if (depot.name) names.set(number, depot.name);
        }
        if (!cancelled) setDepots({ owned, names });
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (!matchesOrdersDailyKpi(row, filter)) return false;
      if (!q) return true;
      return ordersDailyRowSearchText(row).includes(q);
    });
  }, [rows, filter, query]);

  const sorted = useMemo(
    () => sortOrdersDailyRows(filtered, sortField, sortDirection),
    [filtered, sortField, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [filter, query, pageSize, sortField, sortDirection]);

  // Opened from the ETA gauge or the "Late vs ETA" chip: late ones first.
  // A header click afterwards still re-sorts.
  useEffect(() => {
    if (filter.kind === 'eta' || filter.kind === 'eta_late') {
      setSortField('eta_on_time');
      setSortDirection('asc');
    }
  }, [filter]);

  const handleSort = (field: OrdersDailySortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'lifts' || field === 'impact' || field === 'due' ? 'desc' : 'asc');
  };

  return (
    <div className="rounded-lg border border-border bg-card/40 p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-50">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tracking ID, consignment, customer, depot…"
            className="w-full pl-10 pr-3 py-2 bg-secondary border border-border rounded-lg text-sm text-white placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5"
          />
        </div>
        <label className="text-sm text-muted-foreground flex items-center gap-2">
          Show
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-secondary border border-border rounded-lg text-sm text-white px-2 py-1.5"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => downloadCsv(sorted, exportName)}
          disabled={sorted.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-accent disabled:opacity-40 text-white text-sm rounded-lg border border-border"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading order legs…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{emptyMessage}</p>
      ) : (
        <>
          <div className="overflow-x-auto overflow-y-auto rounded-lg border border-border max-h-screen-70">
            <table className="w-full min-w-275 text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 z-20">
                <tr className={`border-b border-border ${STICKY_HEADER_BG}`}>
                  {TABLE_COLUMNS.map(({ field, label, align }) => {
                    const isActive = sortField === field;
                    return (
                      <th
                        key={field}
                        className={`py-2 px-2.5 text-xs uppercase tracking-wider font-semibold whitespace-nowrap ${
                          align === 'right' ? 'text-right' : 'text-left'
                        } ${
                          field === 'tracking_id'
                            ? `sticky left-0 z-30 min-w-44 ${STICKY_HEADER_BG}`
                            : field === 'consignment_no'
                              ? `sticky left-44 z-30 min-w-34 ${STICKY_HEADER_BG} ${STICKY_EDGE_SHADOW}`
                              : STICKY_HEADER_BG
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(field)}
                          title={`Sort by ${label}${isActive ? ` (${sortDirection})` : ''}`}
                          className={`inline-flex items-center gap-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded ${
                            align === 'right' ? 'ml-auto' : ''
                          } ${isActive ? 'text-chart-5' : 'text-muted-foreground hover:text-white'}`}
                        >
                          <span>{label}</span>
                          {isActive && (
                            sortDirection === 'asc'
                              ? <ChevronUp className="w-3.5 h-3.5 shrink-0" aria-hidden />
                              : <ChevronDown className="w-3.5 h-3.5 shrink-0" aria-hidden />
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const impact = formatOrdersImpact(row);
                  const impactLate = row.missed_am || (row.at_risk_1700 && impact !== '—');
                  const rowKey = `${row.grain_date}-${row.tracking_id}-${row.direction}`;
                  const expanded = expandedKey === rowKey;
                  const stickyBg = stickyBodyBg(expanded);
                  return (
                  <Fragment key={rowKey}>
                  <tr
                    onClick={() => setExpandedKey(expanded ? null : rowKey)}
                    className={`group border-b border-border/60 cursor-pointer hover:bg-secondary/60 ${expanded ? 'bg-chart-5/20' : ''}`}
                  >
                    <td className={`sticky left-0 z-raised py-2.5 px-2.5 font-mono whitespace-nowrap min-w-44 ${stickyBg}`}>
                      <span className="inline-flex items-center gap-1.5">
                        {expanded
                          ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
                          : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />}
                        <CopyableOrderId value={row.tracking_id} className="text-chart-10" />
                      </span>
                    </td>
                    <td className={`sticky left-44 z-raised py-2.5 px-2.5 font-mono whitespace-nowrap min-w-34 ${stickyBg} ${STICKY_EDGE_SHADOW}`}>
                      <CopyableOrderId value={row.consignment_no || '—'} className="text-chart-5" />
                    </td>
                    <td
                      className={`py-2.5 px-2.5 max-w-48 truncate ${
                        row.booking_customer_scope === 'not_recorded' || row.booking_customer_scope === 'conflicting'
                          ? 'text-muted-foreground italic'
                          : 'text-foreground'
                      }`}
                      title={bookingCustomerText(row)}
                    >
                      {bookingCustomerText(row) || UNKNOWN_CELL}
                    </td>
                    <td className="py-2.5 px-2.5 text-muted-foreground max-w-48 truncate" title={row.customer || undefined}>
                      {row.customer || '—'}
                    </td>
                    <td className="py-2.5 px-2.5 text-foreground">{row.cust_ref || '—'}</td>
                    <td className="py-2.5 px-2.5 font-mono text-xs text-foreground whitespace-nowrap">
                      {row.destination_postal_code || '—'}
                    </td>
                    <td className="py-2.5 px-2.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-bold tracking-wide ${
                        row.direction === 'collection'
                          ? 'bg-chart-5/15 text-chart-5 border border-chart-5/30'
                          : 'bg-ring/15 text-ring border border-ring/30'
                      }`}>
                        {row.direction === 'collection' ? 'COL' : 'DEL'}
                      </span>
                      {row.direction === 'collection' && row.collection_source && row.collection_source !== 'unknown' && (
                        <span
                          className="block mt-0.5 text-xs text-muted-foreground"
                          title={row.booking_depot
                            ? `Booked by depot ${row.booking_depot}${row.booking_account_code ? ` (${row.booking_account_code})` : ''}`
                            : undefined}
                        >
                          {row.collection_source === 'network' ? 'Network' : 'Own'}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-2.5 text-foreground">{row.type || '—'}</td>
                    <td className="py-2.5 px-2.5 font-mono text-xs whitespace-nowrap">
                      <DepotEnd number={row.col_depot} index={depots} />
                    </td>
                    <td className="py-2.5 px-2.5 font-mono text-xs whitespace-nowrap">
                      <DepotEnd number={row.del_depot} index={depots} />
                    </td>
                    <td className="py-2.5 px-2.5 font-mono text-xs text-chart-1/90 whitespace-nowrap">
                      {formatOrdersService(row)}
                    </td>
                    <td className="py-2.5 px-2.5 text-foreground whitespace-nowrap">
                      {formatOrdersDue(row.due_date, row.due_time)}
                    </td>
                    <td className="py-2.5 px-2.5 text-right tabular-nums text-foreground">
                      {row.lifts == null ? '—' : formatLifts(row.lifts)}
                      {partialDeliveryFraction(row) && (
                        <span className="block text-xs font-normal text-chart-9/80" title={partialDeliveryDetail(row) ?? undefined}>
                          {formatLifts(row.lifts_delivered ?? 0)} done
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-2.5 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded border text-xs font-bold ${statusTagClass(row)}`}>
                        {ordersDailyStatusLabel(row)}
                      </span>
                      {partialDeliveryFraction(row) && (
                        <span
                          className="ml-1 text-xs font-bold text-chart-9/90 tabular-nums"
                          title={partialDeliveryDetail(row) ?? undefined}
                        >
                          {partialDeliveryFraction(row)}
                        </span>
                      )}
                      {row.direction === 'collection' && collectionProvenanceNote(row.collection_stage_provenance) && (
                        <span
                          className="ml-1 text-xs font-bold text-muted-foreground"
                          title={collectionProvenanceNote(row.collection_stage_provenance)!.title}
                        >
                          {collectionProvenanceNote(row.collection_stage_provenance)!.label}
                        </span>
                      )}
                      {row.direction === 'collection'
                        && row.collection_poc_recorded === false
                        && row.collection_stage !== undefined
                        && row.collection_stage !== 'awaiting_collection' && (
                        <span
                          className="ml-1 text-xs font-bold text-muted-foreground"
                          title="No Proof of Collection on record. A depot or onward scan shows it was collected"
                        >
                          no POC
                        </span>
                      )}
                      {row.direction === 'collection' && row.collection_condition && row.collection_condition !== 'on_track' && (
                        <span
                          className={`ml-1 text-xs font-bold ${COLLECTION_CONDITION_TEXT[row.collection_condition]}`}
                          title={COLLECTION_CONDITION_HINTS[row.collection_condition]}
                        >
                          {COLLECTION_CONDITION_LABELS[row.collection_condition]}
                        </span>
                      )}
                      {row.partial_ref_mismatch && (
                        <span
                          className="ml-1 text-xs font-bold text-warning/90"
                          title="POD reference total does not match the booked lift count — pallet split not shown"
                        >
                          ⚠ counts differ
                        </span>
                      )}
                    </td>
                    <td className={`py-2.5 px-2.5 whitespace-nowrap tabular-nums ${
                      row.missed_am ? 'text-destructive font-semibold' : 'text-foreground'
                    }`}>
                      {row.missed_am
                        ? 'Missed'
                        : formatOrdersPod(row.pod_date, row.pod_time)}
                      {sourceStatusContradiction(row) && (
                        <span
                          className="block text-xs font-normal text-warning/80"
                          title={`Palletways reports "${sourceStatusContradiction(row)}" but pallets are still to deliver`}
                        >
                          src: <span className="line-through">{sourceStatusContradiction(row)}</span>
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-2.5 px-2.5 whitespace-nowrap text-xs font-semibold ${
                        row.eta_on_time === false
                          ? 'text-destructive'
                          : row.eta_on_time === true
                            ? 'text-success'
                            : 'text-muted-foreground font-normal'
                      } ${(filter.kind === 'eta' || filter.kind === 'eta_late') && row.eta_on_time === false ? 'ring-1 ring-destructive/40 rounded px-1' : ''}`}
                      title={[
                        row.eta_window_end
                          ? `ETA window ends ${formatOrdersLocalWallInstant(row.eta_window_end)}`
                          : row.eta_at
                            ? `Point ETA ${formatOrdersLocalWallInstant(row.eta_at)}`
                            : undefined,
                        row.eta_actual_at
                          ? `${row.eta_actual_basis === 'arrival' ? 'Driver arrived' : 'POD (no arrival recorded)'} ${formatOrdersLocalWallInstant(row.eta_actual_at)}`
                          : undefined,
                      ].filter(Boolean).join(' · ') || undefined}
                    >
                      {formatOrdersEtaOnTime(row.eta_on_time)}
                      {row.eta_actual_at ? (
                        <span className="block text-xs font-normal text-muted-foreground">
                          {row.eta_actual_basis === 'arrival' ? 'arr' : 'pod'} {formatOrdersLocalWallInstant(row.eta_actual_at)}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2.5 px-2.5 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {row.eta_at
                        ? formatOrdersLocalWallInstant(row.eta_at)
                        : '—'}
                      {row.eta_basis === 'window_end' && row.eta_window_end ? (
                        <span className="block text-xs text-muted-foreground">
                          win {formatOrdersLocalWallInstant(row.eta_window_end)}
                        </span>
                      ) : null}
                    </td>
                    <GpsCheckCell row={row} />
                    <td className={`py-2.5 px-2.5 text-right tabular-nums whitespace-nowrap ${
                      impactLate ? 'text-destructive font-semibold' : 'text-muted-foreground'
                    }`}>
                      {impact}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-border/60">
                      <td colSpan={TABLE_COLUMNS.length} className="p-0">
                        <OrderJourneyTrail row={row} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-muted-foreground">
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded bg-secondary disabled:opacity-40"
                >
                  Previous
                </button>
                <span>Page {safePage} of {totalPages}</span>
                <button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-2 py-1 rounded bg-secondary disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
